import React, { useState } from "react";
import classNames from "classnames";

import { ProductMarketplaceCategory } from "custom-types/Product";

import SolidArrowIcon from "components/Icons/arrows/SolidArrowIcon";

import { GTM_EVENTS, trackGtmEvent } from "../lib/gtm";

export const NAV_LINKS = [
  { href: "/products/cannabis/flower", name: "Flower" },
  { href: "/products/delta-8-thc", name: "Delta-8" },
  { href: "/products/edibles", name: "Edibles" },
  { href: "/products/concentrates/cartridges", name: "Cartridges" },
  { href: "/products/vaping", name: "Vaping" },
  { href: "/products/concentrates", name: "Concentrates" },
  { href: "/products/cannabis/prerolls", name: "Pre-rolls" },
  { href: "/products/hemp-cbd", name: "Hemp CBD" },
  { href: "/products/growing", name: "Growing" },
  { href: "/products/cannabis/seeds", name: "Seeds" },
];

const Overlay = ({
  isSideNavOpen,
  setSideNavOpen,
}: {
  isSideNavOpen: boolean;
  setSideNavOpen: (value: boolean) => void;
}) => (
  <div
    className={classNames(
      "fixed inset-0 z-above-header transition-[opacity] motion-reduce:transition-none bg-black bg-opacity-50",
      {
        "opacity-0 pointer-events-none invisible": !isSideNavOpen,
        "opacity-1 pointer-events-auto visible": isSideNavOpen,
      }
    )}
    onClick={() => setSideNavOpen(false)}
    onKeyPress={() => setSideNavOpen(false)}
    role="button"
    tabIndex={0}
    data-testid="sidenav-overlay"
    aria-label="close side nav"
  />
);

const SideNavigation = ({
  categories,
  currentCategory,
  isCanada,
  isIos,
  isSideNavOpen,
  setSideNavOpen,
}: {
  categories: ProductMarketplaceCategory[];
  currentCategory?: ProductMarketplaceCategory;
  isCanada: boolean;
  isIos: boolean;
  isSideNavOpen: boolean;
  setSideNavOpen: (value: boolean) => void;
}) => {
  const localeCategories = categories.filter(
    (category) => !(isCanada && category.slug === "hemp-cbd")
  );
  const localeDeviceCategories = localeCategories.filter(
    (category) => !(isIos && category.slug === "vaping")
  );

  return (
    <>
      <div
        className={classNames(
          "side-nav bg-white p-lg text-green left-0 top-0 fixed",
          {
            "side-nav--open": isSideNavOpen,
          }
        )}
        data-testid="sidenav"
      >
        <div className="flex justify-between align-center mb-lg">
          <h2 className="font-bold">Browse by category</h2>
          <button
            className="text-green font-bold"
            data-testid="sidenav-close-button"
            onClick={() => setSideNavOpen(false)}
          >
            x
          </button>
        </div>
        <ul className="text-sm">
          {localeDeviceCategories.map(
            (category: ProductMarketplaceCategory) => {
              const href = `/products/${category.slug}`;
              const currentDeviceSubcategories = category.subcategories.filter(
                (subcategory) => !(isIos && subcategory.slug === "cartridges")
              );
              const isActiveCategory =
                currentCategory?.slug === category.slug ||
                currentDeviceSubcategories.some(
                  (s) => s.slug === currentCategory?.slug
                );

              return (
                <React.Fragment key={category.slug}>
                  <li
                    className={classNames("mb-xs", {
                      "font-bold": category.slug === currentCategory?.slug,
                    })}
                  >
                    <a
                      data-testid="sidenav-link"
                      href={href}
                      onClick={() =>
                        trackGtmEvent(GTM_EVENTS.TOP_NAV.DROPDOWN_CLICK, {
                          eventLabel: href,
                        })
                      }
                      onKeyPress={() =>
                        trackGtmEvent(GTM_EVENTS.TOP_NAV.DROPDOWN_CLICK, {
                          eventLabel: href,
                        })
                      }
                    >
                      {category.name}
                    </a>
                  </li>
                  {isActiveCategory &&
                    currentDeviceSubcategories.map((subcategory) => {
                      const href = `/products/${category.slug}/${subcategory.slug}`;

                      return (
                        <li
                          key={subcategory.slug}
                          className={classNames("mb-xs ml-lg", {
                            "font-bold":
                              subcategory.slug === currentCategory?.slug,
                          })}
                        >
                          <a
                            data-testid="sidenav-link"
                            href={href}
                            onClick={() =>
                              trackGtmEvent(GTM_EVENTS.TOP_NAV.DROPDOWN_CLICK, {
                                eventLabel: href,
                              })
                            }
                            onKeyPress={() =>
                              trackGtmEvent(GTM_EVENTS.TOP_NAV.DROPDOWN_CLICK, {
                                eventLabel: href,
                              })
                            }
                          >
                            {subcategory.name}
                          </a>
                        </li>
                      );
                    })}
                </React.Fragment>
              );
            }
          )}
          <li className="mb-xs">
            <a href="/brands">All brands</a>
          </li>
        </ul>
      </div>
      <style jsx>{`
        .side-nav {
          height: 100%;
          min-height: 100vh;
          min-width: 256px;
          overflow-y: scroll;
          transform: translate(-1000px);
          transition: transform 250ms;
          z-index: 121;
        }
        .side-nav--open {
          transform: translate(0);
        }
        .side-nav-item {
          margin-bottom: 8px;
        }
        .side-nav-item--active {
          font-weight: bold;
        }
      `}</style>
    </>
  );
};

const ProductCategoryLinks = ({
  isCanada,
  isIos,
  setSideNavOpen,
}: {
  isCanada: boolean;
  isIos: boolean;
  setSideNavOpen: (value: boolean) => void;
}) => {
  const localeNavLinks = NAV_LINKS.filter(
    (link) =>
      !(isCanada && (link.name === "Hemp CBD" || link.name === "Growing"))
  );
  const localeDeviceNavLinks = localeNavLinks.filter(
    (link) => !(isIos && (link.name === "Vaping" || link.name === "Cartridges"))
  );

  return (
    <>
      <div className="bg-deep-green relative" data-testid="topnav">
        <div className="container mx-none overflow-x-auto overflow-y-hidden pl-lg md:mx-auto md:my-none md:pl-none">
          <ul className="flex font-bold items-center text-green-60 text-sm">
            <li>
              <button
                className="flex mr-lg py-lg relative whitespace-nowrap font-bold"
                data-testid="topnav-categories-button"
                onClick={() => {
                  trackGtmEvent(GTM_EVENTS.TOP_NAV.MORE_CLICK);
                  setSideNavOpen(true);
                }}
              >
                <span>Categories</span>
                <span className="top-nav-icon">
                  <SolidArrowIcon />
                </span>
              </button>
            </li>
            {localeDeviceNavLinks.map((link) => {
              const { name, href } = link;
              return (
                <li key={name}>
                  <a
                    className="flex mr-lg py-lg relative whitespace-nowrap"
                    data-testid="topnav-link"
                    href={href}
                    onClick={() =>
                      trackGtmEvent(GTM_EVENTS.TOP_NAV.NAV_LINK_CLICK, {
                        eventLabel: href,
                      })
                    }
                    onKeyPress={() =>
                      trackGtmEvent(GTM_EVENTS.TOP_NAV.NAV_LINK_CLICK, {
                        eventLabel: href,
                      })
                    }
                  >
                    {name}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <style jsx>{`
        .top-nav-icon {
          width: 24px;
        }
      `}</style>
    </>
  );
};

export const ProductCategoryNavigation: React.FC<{
  categories: ProductMarketplaceCategory[];
  currentCategory?: ProductMarketplaceCategory;
  isCanada?: boolean;
  isIos?: boolean;
}> = ({ categories, currentCategory, isCanada = false, isIos = false }) => {
  const [isSideNavOpen, setSideNavOpen] = useState(false);

  return (
    <>
      <Overlay isSideNavOpen={isSideNavOpen} setSideNavOpen={setSideNavOpen} />
      <SideNavigation
        categories={categories}
        currentCategory={currentCategory}
        isCanada={isCanada}
        isIos={isIos}
        isSideNavOpen={isSideNavOpen}
        setSideNavOpen={setSideNavOpen}
      />
      <ProductCategoryLinks
        isCanada={isCanada}
        isIos={isIos}
        setSideNavOpen={setSideNavOpen}
      />
    </>
  );
};
